import {rockOutline} from './geometry.js';
import {QuarryBackdrop} from './quarry-backdrop.js';

const noise=i=>{const n=Math.sin(i*127.1+311.7)*43758.5453;return n-Math.floor(n);};
export const PAINT=[
  {sky:['#e9e8d7','#b9cec2','#739b9d'],far:'#b1c5b9',middle:'#7ea3a0',near:'#547f83',wall:'#2b4b52',face:'#55716c',stone:'#7c9180',dark:'#203e46',rim:'#d2cbb0',moss:'#728b60',wood:'#c4a378',light:'#eed6a4',metal:'#48616a',rust:'#9b6148',haze:'212,228,209'},
  {sky:['#b7cbd1','#7fa3b2','#466e85'],far:'#92b1bc',middle:'#648d9f',near:'#3e667d',wall:'#243f54',face:'#486977',stone:'#6c8b98',dark:'#172f42',rim:'#a6c1c2',moss:'#687f7c',wood:'#9dafb0',light:'#d9ded0',metal:'#3a586a',rust:'#a06c52',haze:'178,207,218'},
];

// Original illustrations, cached at the actual display density. All surface
// marks move with the object; there is no screen-space noise shimmer.
export class Illustration{
  constructor(renderer){this.r=renderer;this.cache=new Map();this.quarryBackdrop=new QuarryBackdrop(renderer);}
  reset(){this.cache.clear();this.quarryBackdrop?.reset();}
  cached(key,w,h,draw){
    if(this.cache.has(key))return this.cache.get(key);
    const r=this.r,dpr=r.dpr,canvas=document.createElement('canvas');
    canvas.width=Math.ceil(w*dpr);canvas.height=Math.ceil(h*dpr);
    const old=r.ctx,c=canvas.getContext('2d');c.scale(dpr,dpr);r.ctx=c;
    try{draw(c);}finally{r.ctx=old;}
    const sprite={canvas,w,h};this.cache.set(key,sprite);return sprite;
  }
  rock(x,y,width,depth,variant=0){
    const r=this.r,world=r.worldId,p=PAINT[world],pad=24;
    const sprite=this.cached(`rock:${world}:${width.toFixed(2)}:${depth.toFixed(2)}:${variant}`,width+pad*2,depth+pad*2,c=>{
      c.translate(width/2+pad,pad);
      const outline=rockOutline(0,0,width,depth).map(v=>[v.x,-v.y]);
      const shape=points=>points.map(([u,v])=>[u*width,v*depth]);
      const fill=c.createLinearGradient(0,0,width*.22,depth);fill.addColorStop(0,p.face);fill.addColorStop(.33,p.wall);fill.addColorStop(1,p.dark);
      c.save();r.path(outline,fill);c.clip();
      // Broad cut faces precede the smaller seams. Unequal facets follow the
      // fracture directions, rather than striping every rock the same way.
      r.path(shape([[-.5,0],[.08,0],[-.04,.28],[-.16,.46],[-.38,.32]]),p.stone);
      r.path(shape([[.08,0],[.49,0],[.36,.31],[.04,.48],[-.04,.28]]),p.face);
      r.path(shape([[-.38,.32],[-.16,.46],[-.09,.74],[-.25,.69]]),'rgba(11,37,44,.23)');
      r.path(shape([[.36,.31],[.49,.18],[.46,.42],[.15,.53],[.05,.9],[-.01,.63]]),'rgba(10,28,39,.27)');
      r.path(shape([[-.5,.06],[-.08,.11],[.23,.085],[.48,.11],[.48,.16],[.2,.135],[-.1,.15],[-.5,.1]]),'rgba(207,208,171,.16)');
      const seed=variant*77+world*39;
      for(let i=0;i<12;i++){
        const start=-.5+noise(i+seed)*.28,yy=.05+i*.057+noise(i+34)*.024;
        const end=.18+noise(i+seed+17)*.35;
        r.path(shape([[start,yy],[start+.19,yy-.013],[end-.13,yy+.02],[end,yy+.006]]),null,i%3===0?'rgba(197,204,173,.21)':'rgba(13,40,47,.18)',i%3===0?1.1:2,false);
      }
      for(let i=0;i<7;i++){
        const xx=-.43+noise(i+seed+8)*.8,yy=noise(i+seed+95)*.4;
        r.path(shape([[xx,yy],[xx+.028,yy+.12],[xx-.013,yy+.19],[xx+.015,yy+.3]]),null,'rgba(17,41,47,.35)',.9,false);
        r.path(shape([[xx+.007,yy],[xx+.035,yy+.12]]),null,'rgba(224,218,180,.16)',.8,false);
      }
      // Mineral freckles and broken brush-like strokes, fixed per rock.
      for(let i=0;i<110;i++){
        const xx=(noise(i*3+seed)-.5)*width,yy=noise(i*7+19+seed)*depth;
        c.fillStyle=i%4?'rgba(9,33,43,.085)':'rgba(226,222,191,.14)';
        c.fillRect(xx,yy,1+noise(i+54)*6,.5+noise(i+38)*1.5);
      }
      c.globalAlpha=.17;c.fillStyle=c.createPattern(r.grain,'repeat');c.fillRect(-width/2,0,width,depth);c.globalAlpha=1;c.restore();
      // The shared silhouette is retained: surface details never invent a new
      // climbable ledge. Light catches the real top plane and one fracture edge.
      r.path([outline[0],outline[1]],null,p.rim,2.4,false);
      r.path([outline[0],outline.at(-1)],null,'rgba(204,210,178,.3)',1,false);
      r.line(-width/2,2,width/2,2,p.dark,2);
      for(let i=0;i<Math.max(5,width/18);i++){
        const xx=-width/2+(i+.2)*18,ww=9+noise(i+seed)*12;
        r.path([[xx,0],[xx+ww,-.6],[xx+ww-2,3],[xx+1,3]],i%3?p.moss:p.stone);
      }
      if(!world){
        for(let i=0;i<18;i++){
          const xx=(noise(i+seed+5)-.5)*width*.93,len=3+noise(i+29)*10;
          c.beginPath();c.moveTo(xx,0);c.quadraticCurveTo(xx-3,-len*.7,xx-1,-len);c.strokeStyle=i%3?p.moss:'#b8ba88';c.lineWidth=.8;c.stroke();
        }
        for(let i=0;i<3;i++){
          const xx=(-.28+i*.23)*width,vlen=Math.min(depth*.6,14+noise(i+seed)*30);
          c.beginPath();c.moveTo(xx,3);c.bezierCurveTo(xx-3,10,xx+7,vlen*.7,xx+2,vlen);c.strokeStyle='#607956';c.lineWidth=1.3;c.stroke();
          for(let j=0;j<4;j++)r.path([[xx+2,j*vlen/4+6],[xx-3,j*vlen/4+2],[xx-2,j*vlen/4+9]],'#819365');
        }
      }
    });
    r.ctx.drawImage(sprite.canvas,x-width/2-pad,y-pad,sprite.w,sprite.h);
  }
  frame(w,h,index){
    const r=this.r,world=r.worldId,p=PAINT[world],pad=8;
    const sprite=this.cached(`frame:${world}:${w.toFixed(2)}:${h.toFixed(2)}:${index%4}`,w+pad*2,h+pad*2,c=>{
      c.translate(w/2+pad,h/2+pad);c.lineCap='butt';
      const thick=Math.max(3,w*.095);
      const beam=(x1,y1,x2,y2,breadth,tone)=>{
        const dx=x2-x1,dy=y2-y1,d=Math.hypot(dx,dy),nx=-dy/d*breadth/2,ny=dx/d*breadth/2;
        r.path([[x1+nx+1,y1+ny+2],[x2+nx+1,y2+ny+2],[x2-nx+1,y2-ny+2],[x1-nx+1,y1-ny+2]],p.dark);
        r.path([[x1+nx,y1+ny],[x2+nx,y2+ny],[x2-nx,y2-ny],[x1-nx,y1-ny]],tone);
        r.line(x1+nx*.65,y1+ny*.65,x2+nx*.65,y2+ny*.65,p.light,.7);
        r.line(x1-nx*.55,y1-ny*.55,x2-nx*.55,y2-ny*.55,world?'#567583':'#806b53',1);
      };
      beam(-w*.42,-h*.42,w*.42,h*.42,thick*.55,p.metal);
      beam(w*.42,-h*.42,-w*.42,h*.42,thick*.64,world?'#829ea7':'#b39a75');
      beam(-w*.44,-h*.5,-w*.44,h*.5,thick,p.wood);
      beam(w*.44,-h*.5,w*.44,h*.5,thick,p.wood);
      beam(-w*.5,h*.47,w*.5,h*.47,thick*.7,p.metal);
      beam(-w*.51,-h*.48,w*.51,-h*.48,thick,p.wood);
      // Metal collars, inset screws and wood grain remain legible at game size.
      for(const side of [-1,1])for(const end of [-1,1]){
        const x=side*w*.44,y=end*h*.39;
        c.fillStyle=p.metal;c.fillRect(x-thick*.62,y-2.8,thick*1.24,5.6);
        r.line(x-thick*.55,y-2.2,x+thick*.55,y-2.2,'#99a99f',.6);
        r.circle(x,y,1.2,'#d4c6a2',p.dark,.6);
      }
      for(let i=0;i<5;i++){
        const y=-h*.25+i*h*.12,x=(i%2?1:-1)*w*.44;
        r.line(x-.6,y,x+.2,y+3,world?'rgba(205,225,220,.22)':'rgba(87,56,37,.3)',.65);
      }
      if(index%4===1){r.line(w*.36,-h*.47,w*.44,-h*.47,p.rust,2);r.circle(-w*.44,0,1,'#795c44');}
      c.fillStyle='rgba(239,225,180,.75)';c.fillRect(-w*.51,-h*.5-1,w*1.02,1);
    });
    r.ctx.drawImage(sprite.canvas,-w/2-pad,-h/2-pad,sprite.w,sprite.h);
  }
  ridge(points,fill){
    const r=this.r,c=r.ctx;
    r.path(points,fill);c.save();c.clip();
    for(let i=1;i<points.length-2;i++){
      const [x,y]=points[i],[nextX,nextY]=points[i+1],drop=r.h*(.12+noise(i)*.13);
      r.path([[x,y],[x+(nextX-x)*.2,y+drop*.6],[nextX,nextY+drop],[nextX,nextY]],i%2?'rgba(232,237,207,.09)':'rgba(18,70,78,.08)');
    }
    c.restore();
  }
  background(game){
    if(!this.r.worldId){this.quarryBackdrop.draw(game);return;}
    const r=this.r,c=r.ctx,w=r.w,h=r.h,p=PAINT[r.worldId],t=r.clock,drift=-r.cameraX*r.scale*.08;
    const wash=c.createLinearGradient(0,0,w*.2,h);wash.addColorStop(0,p.sky[0]);wash.addColorStop(.62,p.sky[1]);wash.addColorStop(1,p.sky[2]);c.fillStyle=wash;c.fillRect(0,0,w,h);
    const lx=w*.61+drift,ly=h*.19+r.camera*r.scale*.04;
    const bloom=c.createRadialGradient(lx,ly,2,lx,ly,h*.64);bloom.addColorStop(0,r.worldId?'rgba(231,235,217,.38)':'rgba(255,247,204,.68)');bloom.addColorStop(.48,'rgba(231,235,210,.08)');bloom.addColorStop(1,'rgba(230,230,210,0)');c.fillStyle=bloom;c.fillRect(0,0,w,h);
    // Long, asymmetrical clouds leave a bright central corridor.
    for(let i=0;i<5;i++){
      const y=h*(.08+i*.12)+r.camera*r.scale*.035,x=(noise(i+2)*w+t*(i%2?1:-1)*2)%w;
      c.save();c.translate(x,y);c.scale(w*.45,22+i*3);c.beginPath();c.moveTo(-1,0);c.bezierCurveTo(-.5,-.2,-.55,-.6,-.2,-.3);c.bezierCurveTo(.1,-1,.4,-.45,.52,-.18);c.bezierCurveTo(.8,-.2,.9,-.1,1,0);c.closePath();c.fillStyle=`rgba(${p.haze},.2)`;c.fill();c.restore();
    }
    const ridges=[
      [[-.1,.60],[.02,.43],[.085,.46],[.14,.32],[.185,.335],[.21,.29],[.255,.31],[.30,.47],[.34,.42],[.4,.56],[.49,.64],[.60,.56],[.65,.32],[.69,.34],[.72,.25],[.75,.255],[.8,.4],[.85,.35],[.92,.45],[1.1,.29]],
      [[-.1,.7],[.04,.53],[.11,.49],[.16,.57],[.20,.56],[.24,.66],[.31,.69],[.39,.78],[.48,.82],[.59,.79],[.65,.68],[.73,.64],[.78,.52],[.82,.53],[.87,.46],[.91,.49],[1.1,.38]],
      [[-.1,.79],[.035,.65],[.075,.665],[.12,.72],[.175,.73],[.22,.83],[.30,.88],[.39,.92],[.53,.96],[.69,.91],[.79,.77],[.85,.72],[.9,.735],[.96,.65],[1.1,.61]],
    ];
    ridges.forEach((ridge,i)=>{
      const shift=r.camera*r.scale*(.055+i*.06),dx=-r.cameraX*r.scale*(.06+i*.04);
      const pts=ridge.map(([x,y])=>[x*w+dx,y*h+shift]);pts.push([w+500,h+1000],[-500,h+1000]);
      this.ridge(pts,[p.far,p.middle,p.near][i]);
    });
    // Quarry cuts, empty windows and an abandoned haulage trestle tell the story
    // through recognisable structures, placed away from the moving tower.
    c.save();c.translate(w*.19-r.cameraX*r.scale*.12,h*.58+r.camera*r.scale*.14);c.globalAlpha=.36;
    const k=Math.min(w/1440,h/900);c.scale(k,k);
    for(let i=0;i<5;i++){
      r.path([[-145-i*13,24+i*19],[-20-i*15,24+i*19],[-8-i*15,34+i*19],[-150-i*13,34+i*19]],p.wall);
    }
    for(let x=-125;x<=30;x+=30){r.line(x,18,x+13,74,p.wall,3);r.line(x,18,x+32,18,p.wall,3);r.line(x,20,x+34,68,p.wall,1);}
    c.restore();
    c.save();c.translate(w*.83-r.cameraX*r.scale*.17,h*.57+r.camera*r.scale*.15);c.scale(Math.max(.5,k),Math.max(.5,k));c.globalAlpha=.46;
    r.path([[-55,0],[36,0],[41,52],[-60,52]],p.wall);r.path([[-65,0],[-21,-24],[44,-5],[45,2]],p.near);
    for(let i=0;i<5;i++)c.fillStyle=p.far,c.fillRect(-47+i*16,12,8,14);
    r.line(-52,50,-58,90,p.wall,5);r.line(31,50,36,92,p.wall,5);r.line(-58,65,37,91,p.wall,2);
    if(r.worldId){
      c.save();c.translate(-4,-32);c.rotate(-.3);c.beginPath();c.moveTo(-64,-18);c.quadraticCurveTo(0,62,64,-18);c.quadraticCurveTo(0,3,-64,-18);c.closePath();c.fillStyle=p.wall;c.fill();
      c.beginPath();c.ellipse(0,-18,64,10,0,0,Math.PI*2);c.strokeStyle=p.middle;c.lineWidth=2;c.stroke();r.line(-43,-14,12,24,p.near,1);r.line(43,-14,12,24,p.near,1);r.line(10,19,27,-69,p.wall,3);r.circle(27,-69,4,p.wall);c.restore();
    }else{
      r.line(-28,-4,-32,-129,p.wall,7);r.line(-32,-129,-150,-95,p.wall,6);r.line(-150,-95,-31,-167,p.wall,2);r.line(-31,-167,-32,-129,p.wall,3);r.line(-146,-96,-146,27,p.wall,1);r.path([[-164,26],[-128,26],[-134,40],[-158,40]],p.wall);
      for(let i=0;i<5;i++)r.line(-32,-124+i*20,-15,-103+i*20,p.wall,1.5);
    }
    c.restore();
    if(!r.worldId){
      for(const side of [-1,1])for(let i=0;i<8;i++){
        const x=(side<0?.1:.89)*w+(noise(i+12)-.5)*w*.15-r.cameraX*r.scale*.19,y=h*(.82+noise(i+25)*.11)+r.camera*r.scale*.18,size=(12+noise(i+59)*25)*Math.min(w/900,1);
        r.path([[x,y-size],[x-size*.28,y-size*.44],[x-size*.13,y-size*.44],[x-size*.4,y],[x+size*.38,y],[x+size*.13,y-size*.44],[x+size*.27,y-size*.44]],p.near);
      }
    }
    // One broad mist layer bridges the value planes; no repeating horizontal bars.
    const mist=c.createLinearGradient(0,h*.42,0,h);mist.addColorStop(0,`rgba(${p.haze},0)`);mist.addColorStop(.58,`rgba(${p.haze},.17)`);mist.addColorStop(1,`rgba(${p.haze},.03)`);c.fillStyle=mist;c.fillRect(0,h*.42,w,h*.58);
    this.walls(p);
    r.circle(lx,ly,1.7,'#fff1c4');
    for(let i=0;i<(r.worldId?46:19);i++){
      const x=(noise(i)*w+t*(r.worldId?55+game.wind*90:4))%(w+80)-40,y=(noise(i+51)*h+t*(r.worldId?140:-5)+h*100)%(h+50)-25;
      if(r.worldId)r.line(x,y,x+4+game.wind*15,y-11,'rgba(206,231,232,.19)',.7);
      else r.circle(x,y,.5+noise(i+42),i%4?'rgba(240,237,198,.28)':'rgba(255,229,171,.55)');
    }
  }
  walls(p){
    const r=this.r,c=r.ctx,w=r.w,h=r.h,dy=r.camera*r.scale*.23;
    for(const side of [-1,1]){
      c.save();c.translate(side<0?0:w,0);c.scale(side<0?1:-1,1);
      const outline=[[0,-500],[w*.046,-500],[w*.075,h*.08+dy],[w*.045,h*.23+dy],[w*.105,h*.34+dy],[w*.095,h*.46+dy],[w*.147,h*.63+dy],[w*.12,h*.8+dy],[w*.17,h*1.1+dy],[0,h*2+dy]];
      r.path(outline,p.wall);c.save();c.clip();
      r.path([[0,-50],[w*.047,0],[w*.021,h*.2+dy],[w*.105,h*.34+dy],[w*.07,h*.49+dy],[w*.135,h*.68+dy],[0,h*.9+dy]],p.face);
      r.path([[0,h*.15+dy],[w*.045,h*.23+dy],[w*.025,h*.54+dy],[w*.08,h*.73+dy],[0,h]],'rgba(18,41,46,.25)');
      for(let i=0;i<20;i++){
        const y=i*h*.073+dy-h*.3;
        r.path([[0,y],[w*.047,y-9],[w*.074,y+5],[w*.16,y-2]],null,i%3?'rgba(10,34,44,.18)':'rgba(199,208,177,.12)',i%3?2:1,false);
      }
      c.restore();r.path(outline.slice(1,-1),null,'rgba(183,201,179,.14)',1,false);c.restore();
    }
  }
}
